import { uiText, currentLocale } from '@/i18n/interface';

import { supabase } from '../integrations/supabase/client';
import { toast } from "@/hooks/use-toast";

interface SessionData {
  id: string;
  title: string;
  date: string;
  start_time: string;
  end_time: string;
  description?: string | null;
}

interface NotificationParams {
  sessionData: SessionData;
  sessionType: 'training_session' | 'promotion_session';
  ageGroup: 'all' | 'kids' | 'teens' | 'adults';
  title: string;
  date: string;
  startTime: string;
  endTime: string;
  description: string;
  sendNotification: (data: any) => Promise<boolean>;
}

export const sendAgeBasedNotifications = async ({
  sessionData,
  sessionType,
  ageGroup,
  title,
  date,
  startTime,
  endTime,
  description,
  sendNotification
}: NotificationParams) => {
  console.log('Sending notifications for age group:', ageGroup);

  // Get students based on age group with proper filtering
  let studentsQuery = supabase
    .from('profiles')
    .select('id, email, first_name, last_name, age')
    .eq('role', 'student');

  // Apply age filtering based on selection
  if (ageGroup === 'kids') {
    // Kids: age between 5-12
    studentsQuery = studentsQuery.not('age', 'is', null).gte('age', 5).lte('age', 12);
  } else if (ageGroup === 'teens') {
    // Teens: age between 13-17
    studentsQuery = studentsQuery.not('age', 'is', null).gte('age', 13).lte('age', 17);
  } else if (ageGroup === 'adults') {
    // Adults: age 18+ OR age not specified (null)
    studentsQuery = studentsQuery.or('age.gte.18,age.is.null');
  }
  // For 'all', no additional filtering is applied

  const { data: students, error } = await studentsQuery;

  if (error) {
    console.error('Error fetching students for notifications:', error);
    return;
  }

  console.log('Found students:', students?.length, 'for age group:', ageGroup);

  if (!students || students.length === 0) {
    const ageGroupText = ageGroup === 'kids' ? uiText("Kids (5-12)") : ageGroup === 'teens' ? uiText("Teens (13-17)") : ageGroup === 'adults' ? uiText("Adults (18+)") : 'selected';
    toast({
      title: uiText("Notice"),
      description: uiText("No students found in the {{value0}} age group.", { value0: ageGroupText })
    });
    return;
  }

  // Create notifications in the database for filtered students
  const notifications = students.map(student => ({
    user_id: student.id,
    type: sessionType === 'training_session' ? 'training' : 'promotion',
    title: title,
    message: uiText("A new {{value0}} has been scheduled for {{value1}} at {{value2}}{{value3}}", { value0: sessionType === 'training_session' ? uiText("training session") : uiText("belt promotion session"), value1: new Date(date).toLocaleDateString(currentLocale()), value2: startTime, value3: ageGroup !== 'all' ? ` (${ageGroup === 'kids' ? 'Kids 5-12' : ageGroup === 'teens' ? 'Teens 13-17' : 'Adults 18+'})` : '' }),
    session_id: sessionType === 'training_session' ? sessionData.id : null,
    promotion_session_id: sessionType === 'promotion_session' ? sessionData.id : null
  }));

  const { error: notificationError } = await supabase
    .from('notifications')
    .insert(notifications);

  if (notificationError) {
    console.error('Error creating notifications:', notificationError);
  }

  // Send email notifications using the edge function
  try {
    await sendNotification({
      type: sessionType,
      title: title,
      message: uiText("A new {{value0}} has been scheduled{{value1}}.", { value0: sessionType === 'training_session' ? uiText("training session") : uiText("belt promotion session"), value1: ageGroup !== 'all' ? ` for ${ageGroup === 'kids' ? 'Kids (5-12)' : ageGroup === 'teens' ? 'Teens (13-17)' : 'Adults (18+)'}` : '' }),
      sessionData: {
        date,
        start_time: startTime,
        end_time: endTime,
        description: description.trim() || undefined
      },
      ageFilter: ageGroup
    });
  } catch (emailError) {
    console.error('Error sending email notifications:', emailError);
  }
};
